import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { CogIcon } from '@heroicons/react/24/outline';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    instructorRegistrationEnabled: true,
    referralSettings: {
      referrerEarning: 100,
      referredUserEarning: 50,
      enabled: true
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/admin/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReferralSettingChange = async (field, value) => {
    const updatedReferralSettings = {
      ...settings.referralSettings,
      [field]: value
    };

    setSettings(prev => ({
      ...prev,
      referralSettings: updatedReferralSettings
    }));

    setSaving(true);
    try {
      await axios.put('/api/referrals/settings', updatedReferralSettings);
      toast.success('Referral settings updated successfully');
    } catch (error) {
      console.error('Error updating referral settings:', error);
      toast.error('Failed to update referral settings');
      // Revert on error
      setSettings(prev => ({
        ...prev,
        referralSettings: settings.referralSettings
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleReferralSystem = () => {
    handleReferralSettingChange('enabled', !settings.referralSettings.enabled);
  };

  const updateSetting = async (key, value) => {
    setSaving(true);
    try {
      const response = await axios.put('/api/admin/settings', { [key]: value });
      setSettings(prev => ({ ...prev, [key]: value }));
      toast.success('Setting updated successfully');
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleInstructorRegistration = () => {
    updateSetting('instructorRegistrationEnabled', !settings.instructorRegistrationEnabled);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center">
          <CogIcon className="h-8 w-8 text-gray-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Admin Settings</h1>
        </div>
        <p className="text-gray-600 mt-2">Configure system-wide settings</p>
      </div>

      <div className="space-y-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Instructor Registration Settings
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">
                    Allow New Instructor Registrations
                  </h4>
                  <p className="text-sm text-gray-500 mt-1">
                    When disabled, users cannot register as instructors. Existing instructors can still log in and create courses.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    type="button"
                    onClick={handleToggleInstructorRegistration}
                    disabled={saving}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      settings.instructorRegistrationEnabled ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.instructorRegistrationEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {saving && (
              <div className="mt-4">
                <div className="text-sm text-gray-500">Saving...</div>
              </div>
            )}
          </div>
        </div>

        {/* Referral Settings */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Referral Bonus Settings
            </h3>

            <div className="space-y-6">
              {/* Enable/Disable Referral System */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">
                    Enable Referral & Earn System
                  </h4>
                  <p className="text-sm text-gray-500 mt-1">
                    When disabled, users cannot earn referral bonuses and referral features will be hidden.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    type="button"
                    onClick={handleToggleReferralSystem}
                    disabled={saving}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      settings.referralSettings.enabled ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.referralSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Referrer Earning */}
              <div>
                <label htmlFor="referrerEarning" className="block text-sm font-medium text-gray-700">
                  Referrer Bonus Amount (₹)
                </label>
                <div className="mt-1">
                  <input
                    type="number"
                    id="referrerEarning"
                    min="0"
                    value={settings.referralSettings.referrerEarning}
                    onChange={(e) => handleReferralSettingChange('referrerEarning', parseInt(e.target.value) || 0)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Amount credited to the user's wallet when their referral successfully enrolls in a course.
                  </p>
                </div>
              </div>

              {/* Referred User Earning */}
              <div>
                <label htmlFor="referredUserEarning" className="block text-sm font-medium text-gray-700">
                  Referred User Bonus Amount (₹)
                </label>
                <div className="mt-1">
                  <input
                    type="number"
                    id="referredUserEarning"
                    min="0"
                    value={settings.referralSettings.referredUserEarning}
                    onChange={(e) => handleReferralSettingChange('referredUserEarning', parseInt(e.target.value) || 0)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Welcome bonus amount credited to the new user's wallet when they successfully enroll in a course.
                  </p>
                </div>
              </div>
            </div>

            {saving && (
              <div className="mt-4">
                <div className="text-sm text-gray-500">Saving...</div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white shadow rounded-lg hover:shadow-md transition-shadow duration-200">
          <div className="px-4 py-5 sm:p-6 text-center">
            <svg className="h-12 w-12 mx-auto text-blue-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900">Banner Management</h3>
            <p className="text-sm text-gray-600 mt-2">
              Manage promotional banners and announcements
            </p>
            <a
              href="/admin/banners"
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Manage Banners
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
